import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef
} from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,

  Stack,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  AddCircleOutline as AddIcon, // For Logic tab placeholder
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { DragEndEvent } from '@dnd-kit/core'; // Explicit import for DragEndEvent

// Contexts
import { useHeaderContext } from '@/contexts/HeaderContext';
import { useAppUser } from '@/contexts/AppUserProvider';
import { UnifiedTabs, UnifiedTabsContainer } from '@/components/UnifiedTabs';

// Services
import * as templateService from '@/services/templateService';
import * as questionService from '@/services/questionService';
import { createSessionAndNavigate } from '@/services/sessionNavigationService';
import {
  createTemplateSummary,
  updateTemplateSummary,
  deleteTemplateSummary,
  getTemplateSummaries
} from '@/services/templateSummaryService';
import {
  CreateTemplateSummaryInput,
  UpdateTemplateSummaryInput
} from '@/types/templateSummaryTypes';
import {
  CreateQuestionInput,
  QuestionType, // Keep QuestionType import
  ReorderQuestionsInput,
  UpdateQuestionInput
} from '@/types/questionTypes';
import { UpdateTemplateInput } from '@/types/templateTypes';
import { TemplateCategory } from '@/types/templateCategoryTypes';
import { DistributionGroup } from '@/types/distributionGroupTypes';

// Local Imports
import { UiTemplate, UiQuestion } from './templateEditTypes';
import { getTypeLabel } from './templateEditUtils';
import { getAllTemplateCategories } from '@/services/templateCategoryService';
import { arrayMove } from '@dnd-kit/sortable';
import QuestionList from '@/components/TemplateEditPageComponents/QuestionList';
import TemplateSettingsForm from '@/components/TemplateEditPageComponents/TemplateSettingsForm';
import TemplateSummaryCard from '@/components/TemplateEditPageComponents/TemplateSummaryCard';
import { SummaryList, SummaryListRef } from '@/components/TemplateEditPageComponents/SummaryList';
import { Plus, Article } from '@phosphor-icons/react';

export const TemplateEditPage: React.FC = () => {
  const { id: templateIdFromParams } = useParams<{ id: string }>();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser } = useAppUser();

  const [template, setTemplate] = useState<UiTemplate | null>(null);
  const [originalTemplate, setOriginalTemplate] = useState<UiTemplate | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'success' | 'error' | 'saving'
  >('idle');
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedQuestions, setExpandedQuestions] = useState<
    (number | string)[]
  >([]);
  const [questionSearchTerm, setQuestionSearchTerm] = useState('');
  
  // Ref for SummaryList to trigger add dialog
  const summaryListRef = React.useRef<SummaryListRef>(null);

  const [templateCategories, setTemplateCategories] = useState<
    TemplateCategory[]
  >([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isEditTemplateDialogOpen, setIsEditTemplateDialogOpen] =
    useState(false);

  const savingRef = useRef(false);

  const hasChanges = useMemo(() => {
    if (saveStatus === 'saving') return true;
    return JSON.stringify(template) !== JSON.stringify(originalTemplate);
  }, [template, originalTemplate, saveStatus]);

  const loadData = async () => {
    if (!templateIdFromParams || !appUser?.id) {
      setIsLoading(false);
      setError(
        templateIdFromParams
          ? 'User not authenticated.'
          : 'Template ID is missing.'
      );
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const fetchedTemplate = await templateService.getTemplateById(
        Number(templateIdFromParams),
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      if (fetchedTemplate) {
        // **** FIX FOR SORTING: Sort questions by order attribute ****
        const sortedApiQuestions = (fetchedTemplate.questions || []).sort(
          (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0) // Handle undefined order
        );

        const uiTemplateData: UiTemplate = {
          ...fetchedTemplate,
          questions: sortedApiQuestions.map((q: any) => ({
            // q is from service, map to UiQuestion
            id: q.id, // Ensure ID is explicitly mapped
            type: q.questionType as QuestionType,
            title: q.title,
            description: q.description,
            required: q.isRequired,
            options: (q.options || []).map((opt: any) => ({
              // opt is from service
              id: opt.id, // Ensure option ID
              label: opt.label,
              order: opt.order
            })),
            hasOther: q.hasOtherOption,
            order: q.order, // Ensure order is mapped
            createdAtUtc: q.createdAtUtc,
            updatedAtUtc: q.updatedAtUtc
          })) as UiQuestion[], // Cast to UiQuestion[]
          summaries: fetchedTemplate.summaries || [],
          // Populate category object for summary card display
          category:
            templateCategories.find(
              (cat) => cat.id === fetchedTemplate.templateCategoryId
            ) || undefined
        };
        setTemplate(uiTemplateData);
        setOriginalTemplate(JSON.parse(JSON.stringify(uiTemplateData))); // Deep copy
      } else {
        setError('Template not found or access denied.');
      }
    } catch (err: any) {
      console.error('Failed to load template:', err);
      setError(err.message || 'Failed to load template data.');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Template Summary Functions ---
  const refreshTemplateSummaries = async () => {
    if (!template || !appUser?.id) return;

    try {
      const summaries = await getTemplateSummaries(
        template.id,
        appUser.id,
        () => getAccessTokenSilently() as Promise<string>,
        () => getAccessTokenWithPopup() as Promise<string>
      );

      setTemplate((prev) => (prev ? { ...prev, summaries } : null));
    } catch (error: any) {
      console.error('Failed to refresh template summaries:', error);
    }
  };

  const handleAddSummary = async (summaryInput: CreateTemplateSummaryInput) => {
    if (!appUser?.id) {
      console.error('User not authenticated');
      return;
    }

    try {
      setSaveStatus('saving');
      const newSummary = await createTemplateSummary(
        summaryInput,
        appUser.id,
        () => getAccessTokenSilently() as Promise<string>,
        () => getAccessTokenWithPopup() as Promise<string>
      );

      // Add the new summary to the current template
      setTemplate((prev) =>
        prev
          ? {
              ...prev,
              summaries: [...(prev.summaries || []), newSummary]
            }
          : null
      );

      setSaveStatus('success');
      console.log('Summary created successfully:', newSummary);
    } catch (error: any) {
      console.error('Error creating summary:', error);
      setError(error.message || 'Failed to create summary');
      setSaveStatus('error');
    } finally {
      // Reset to idle after a delay to show success state briefly
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const handleUpdateSummary = async (
    summaryInput: UpdateTemplateSummaryInput
  ) => {
    if (!appUser?.id) {
      console.error('User not authenticated');
      return;
    }

    try {
      setSaveStatus('saving');
      const updatedSummary = await updateTemplateSummary(
        Number(templateIdFromParams),
        summaryInput,
        appUser.id,
        () => getAccessTokenSilently() as Promise<string>,
        () => getAccessTokenWithPopup() as Promise<string>
      );

      // Update the summary in the current template
      setTemplate((prev) =>
        prev
          ? {
              ...prev,
              summaries: (prev.summaries || []).map((s) =>
                s.id === updatedSummary.id ? updatedSummary : s
              )
            }
          : null
      );

      setSaveStatus('success');
      console.log('Summary updated successfully:', updatedSummary);
    } catch (error: any) {
      console.error('Error updating summary:', error);
      setError(error.message || 'Failed to update summary');
      setSaveStatus('error');
    } finally {
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const handleDeleteSummary = async (summaryId: number) => {
    if (!appUser?.id || !template) {
      console.error('User not authenticated or template not loaded');
      return;
    }

    try {
      setSaveStatus('saving');
      await deleteTemplateSummary(
        template.id,
        summaryId,
        appUser.id,
        () => getAccessTokenSilently() as Promise<string>,
        () => getAccessTokenWithPopup() as Promise<string>
      );

      // Remove the summary from the current template
      setTemplate((prev) =>
        prev
          ? {
              ...prev,
              summaries: (prev.summaries || []).filter(
                (s) => s.id !== summaryId
              )
            }
          : null
      );

      setSaveStatus('success');
      console.log('Summary deleted successfully');
    } catch (error: any) {
      console.error('Error deleting summary:', error);
      setError(error.message || 'Failed to delete summary');
      setSaveStatus('error');
    } finally {
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  // Update summary distribution groups locally when child components confirm updates
  const handleSummaryGroupsUpdated = useCallback(
    (summaryId: number, groups: DistributionGroup[]) => {
      setTemplate((prev) =>
        prev
          ? {
              ...prev,
              summaries: (prev.summaries || []).map((s) =>
                s.id === summaryId ? { ...s, distributionGroups: groups } : s
              )
            }
          : prev
      );
    },
    []
  );

  // --- Data Fetching (Template) ---
  useEffect(() => {
    if (appUser) {
      loadData();
    } else {
      if (!templateIdFromParams) {
        setIsLoading(false);
        setError('Template ID is missing.');
      }
    }
  }, [
    templateIdFromParams,
    appUser,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  // Ensure summaries (with distribution groups) are refreshed when switching to Summaries tab
  useEffect(() => {
    if (activeTab === 'summaries') {
      refreshTemplateSummaries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // --- Data Fetching (Template Categories) ---
  useEffect(() => {
    const loadCategories = async () => {
      if (!appUser?.id) return;
      setIsLoadingCategories(true);
      try {
        const categories = await getAllTemplateCategories(
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        setTemplateCategories(categories);
        // If template is already loaded, update its category object
        setTemplate((prevTemplate) => {
          if (
            prevTemplate &&
            prevTemplate.templateCategoryId &&
            !prevTemplate.category
          ) {
            const matchingCategory = categories.find(
              (cat) => cat.id === prevTemplate.templateCategoryId
            );
            if (matchingCategory) {
              return { ...prevTemplate, category: matchingCategory };
            }
          }
          return prevTemplate;
        });
      } catch (err: any) {
        console.error('Failed to load template categories:', err);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    if (appUser) {
      loadCategories();
    }
  }, [appUser, getAccessTokenSilently, getAccessTokenWithPopup]);

  const filteredQuestions = useMemo(() => {
    if (!template) return [];
    // The template.questions should already be sorted due to loadData and subsequent operations
    if (!questionSearchTerm) return template.questions;
    const lowerSearch = questionSearchTerm.toLowerCase();
    return template.questions.filter(
      (q) =>
        q.title.toLowerCase().includes(lowerSearch) ||
        q.description?.toLowerCase().includes(lowerSearch) ||
        getTypeLabel(q.type).toLowerCase().includes(lowerSearch)
    );
  }, [template, questionSearchTerm]); // template.questions is now part of template

  const filteredQuestionIds = useMemo(
    () => filteredQuestions.map((q) => q.id),
    [filteredQuestions]
  );

  const toggleQuestionExpand = useCallback((questionId: number | string) => {
    setExpandedQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  }, []);

  const expandAllQuestions = useCallback(() => {
    setExpandedQuestions(filteredQuestionIds);
  }, [filteredQuestionIds]);

  const collapseAllQuestions = useCallback(() => {
    setExpandedQuestions([]);
  }, []);

  const handleLocalQuestionChange = useCallback(
    (questionId: number | string, updates: Partial<UiQuestion>) => {
      setTemplate((prev) => {
        if (!prev) return null;
        const updatedQuestions = prev.questions.map((q) =>
          q.id === questionId ? { ...q, ...updates } : q
        );
        return { ...prev, questions: updatedQuestions };
      });
      setSaveStatus('idle');
      setError(null);
    },
    []
  );

  const handlePersistQuestion = useCallback(
    async (questionId: number | string, updates: Partial<UiQuestion>) => {
      if (!template || !appUser?.id || typeof template.id !== 'number') {
        setError('Cannot save: Missing template or user information.');
        return;
      }
      const questionToUpdate = template.questions.find(
        (q) => q.id === questionId
      );
      if (!questionToUpdate) {
        setError('Cannot save: Question not found.');
        return;
      }

      while (savingRef.current) {
        console.log('Waiting for save to finish...');
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      const payload = { ...questionToUpdate, ...updates };
      setSaveStatus('saving');
      setIsSaving(true);
      savingRef.current = true;
      setError(null);

      try {
        if (typeof payload.id === 'number') {
          const apiInput: UpdateQuestionInput = {
            questionId: payload.id as number,
            title: payload.title,
            description: payload.description,
            questionType: payload.type,
            isRequired: payload.required,
            order: payload.order,
            hasOtherOption: payload.hasOther,
            options: payload.options?.map((opt) => ({
              id: typeof opt.id === 'number' ? opt.id : null,
              label: opt.label,
              order: opt.order
            }))
          };
          await questionService.updateQuestion(
            template.id,
            payload.id as number,
            apiInput,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
        } else {
          // This case should ideally be handled by a dedicated "create" flow
          // initiated by the Add Question button, not inline persistence of a temp question.
          // The addQuestion function below handles creation properly.
          console.warn(
            'Attempting to persist a question with a temporary ID via update handler.'
          );
          setError(
            "Cannot save new question using this method, please use 'Add Question'"
          );
          setSaveStatus('error');
          setIsSaving(false);
          savingRef.current = false;

          return;
        }

        setSaveStatus('idle');
      } catch (err: any) {
        console.error('Failed to update question via API:', err);
        setError(err.message || 'Failed to save question.');
        setSaveStatus('error');
        loadData(); // Reload data on failure only
      } finally {
        setIsSaving(false);
        savingRef.current = false;
      }
    },
    [template, appUser, getAccessTokenSilently, getAccessTokenWithPopup]
  );

  const updateTemplateDetailsLocal = useCallback(
    (updates: Partial<UiTemplate>) => {
      setTemplate((prev) => (prev ? { ...prev, ...updates } : null));
      setSaveStatus('idle');
      setError(null);
    },
    []
  );

  const handleSaveTemplateSettings = useCallback(
    async (immediateUpdates?: Partial<UiTemplate>) => {
      if (!template || !appUser?.id || typeof template.id !== 'number') {
        setError('Cannot save: Missing template or user information.');
        return;
      }
      const templateToSave = { ...template, ...(immediateUpdates || {}) };

      while (savingRef.current) {
        console.log('Waiting for save to finish...');
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      savingRef.current = true;
      setSaveStatus('saving');
      setIsSaving(true);
      setError(null);

      try {
        const updateInput: UpdateTemplateInput = {
          id: templateToSave.id,
          name: templateToSave.name,
          description: templateToSave.description,
          templateCategoryId: templateToSave.templateCategoryId,
          titleGenerationPrompt: templateToSave.titleGenerationPrompt,
          isArchived: templateToSave.isArchived,
          requiresApproval: templateToSave.requiresApproval
        };
        const updatedApiTemplate = await templateService.updateTemplate(
          templateToSave.id,
          updateInput,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        if (updatedApiTemplate) {
          const categoryForUpdate = templateCategories.find(
            (tc) => tc.id === updatedApiTemplate.templateCategoryId
          );
          const uiTemplateData: UiTemplate = {
            ...templateToSave, // local state including questions
            ...updatedApiTemplate, // API confirmed fields for template itself
            category: categoryForUpdate || templateToSave.category, // Update category object
            questions: templateToSave.questions // Keep existing questions state
          };
          setTemplate(uiTemplateData);
          setOriginalTemplate(JSON.parse(JSON.stringify(uiTemplateData)));
          setSaveStatus('idle');
        } else {
          setSaveStatus('error');
          setError('Failed to save template settings: No response.');
        }
      } catch (err: any) {
        console.error('Failed to save template settings:', err);
        setSaveStatus('error');
        setError(err.message || 'Failed to save template settings.');
      } finally {
        setIsSaving(false);
        savingRef.current = false;
      }
    },
    [
      template,
      appUser,
      getAccessTokenSilently,
      getAccessTokenWithPopup,
      templateCategories
    ]
  );

  const addQuestion = useCallback(
    async (type: QuestionType) => {
      if (!template || !appUser?.id || typeof template.id !== 'number') return;

      const newQuestionOrder = template.questions.length;
      const apiInput: CreateQuestionInput = {
        questionType: type,
        title: `New ${getTypeLabel(type)} Question`,
        description: '',
        isRequired: false,
        order: newQuestionOrder,
        hasOtherOption: false,
        options:
          type === QuestionType.Checkbox ||
          type === QuestionType.Radio ||
          type === QuestionType.Dropdown
            ? [{ label: 'Option 1', order: 0 }]
            : []
      };

      setIsSaving(true);
      setSaveStatus('saving');
      setError(null);
      try {
        const savedQuestion = await questionService.createQuestion(
          template.id,
          apiInput,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        const newUiQuestion: UiQuestion = {
          // Map API response to UiQuestion
          id: savedQuestion.id,
          type: savedQuestion.questionType as QuestionType,
          title: savedQuestion.title,
          description: savedQuestion.description,
          required: savedQuestion.isRequired,
          options: (savedQuestion.options || []).map((opt) => ({
            ...opt,
            id: opt.id
          })),
          hasOther: savedQuestion.hasOtherOption,
          order: savedQuestion.order,
          createdAtUtc: savedQuestion.createdAtUtc,
          updatedAtUtc: savedQuestion.updatedAtUtc
        };

        setTemplate((prev) => {
          if (!prev) return null;
          const updatedQuestions = [...prev.questions, newUiQuestion].sort(
            (a, b) => a.order - b.order
          );
          const newState = { ...prev, questions: updatedQuestions };
          setOriginalTemplate(JSON.parse(JSON.stringify(newState)));
          return newState;
        });

        setTimeout(() => {
          setExpandedQuestions((prevExpanded) => [
            ...prevExpanded,
            newUiQuestion.id
          ]);
          document
            .getElementById(String(newUiQuestion.id)) // DOM ID needs to be string
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Focus the title input field of the new question
          setTimeout(() => {
            const titleInput = document.querySelector(
              `[data-question-id="${newUiQuestion.id}"] input[placeholder*="title" i], [data-question-id="${newUiQuestion.id}"] input[type="text"]:first-of-type`
            );
            if (titleInput instanceof HTMLInputElement) {
              titleInput.focus();
              titleInput.select(); // Select the default text so user can start typing immediately
            }
          }, 200);
        }, 100);
        setSaveStatus('idle');
      } catch (err: any) {
        console.error('Failed to add question:', err);
        setError(err.message || 'Failed to add question.');
        setSaveStatus('error');
      } finally {
        setIsSaving(false);
      }
    },
    [template, appUser, getAccessTokenSilently, getAccessTokenWithPopup]
  );

  const duplicateQuestion = useCallback(
    async (questionId: number | string) => {
      if (!template || !appUser?.id || typeof template.id !== 'number') return;
      const questionToDuplicate = template.questions.find(
        (q) => q.id === questionId
      );
      if (!questionToDuplicate) return;

      const newOrderForDuplicated =
        template.questions.findIndex((q) => q.id === questionId) + 1;

      const apiInput: CreateQuestionInput = {
        questionType: questionToDuplicate.type,
        title: `${questionToDuplicate.title} (Copy)`,
        description: questionToDuplicate.description,
        isRequired: questionToDuplicate.required,
        // Order will be correctly set after insertion and reordering all
        order: newOrderForDuplicated, // Tentative order, will be adjusted
        hasOtherOption: questionToDuplicate.hasOther,
        options:
          questionToDuplicate.options?.map((opt) => ({
            label: opt.label,
            order: opt.order
          })) || []
      };

      setIsSaving(true);
      setSaveStatus('saving');
      setError(null);
      try {
        const savedQuestion = await questionService.createQuestion(
          template.id,
          apiInput, // This will create the question at a temporary order
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        const newUiQuestion: UiQuestion = {
          id: savedQuestion.id,
          type: savedQuestion.questionType as QuestionType,
          title: savedQuestion.title,
          description: savedQuestion.description,
          required: savedQuestion.isRequired,
          options: (savedQuestion.options || []).map((opt) => ({
            ...opt,
            id: opt.id
          })),
          hasOther: savedQuestion.hasOtherOption,
          order: savedQuestion.order, // Use order from API response initially
          createdAtUtc: savedQuestion.createdAtUtc,
          updatedAtUtc: savedQuestion.updatedAtUtc
        };

        // Insert and reorder locally
        let newQuestionsList: UiQuestion[] = [];
        setTemplate((prev) => {
          if (!prev) return null;
          const insertAtIndex =
            prev.questions.findIndex((q) => q.id === questionId) + 1;
          const tempList = [...prev.questions];
          tempList.splice(insertAtIndex, 0, newUiQuestion);
          newQuestionsList = tempList.map((q, index) => ({
            ...q,
            order: index
          }));

          const newState = { ...prev, questions: newQuestionsList };
          setOriginalTemplate(JSON.parse(JSON.stringify(newState)));
          return newState;
        });

        // Then call API to reorder all questions based on the new list
        const reorderInput: ReorderQuestionsInput = {
          orderedQuestionIds: newQuestionsList
            .filter((q) => typeof q.id === 'number') // Only persisted questions
            .map((q) => ({ questionId: q.id as number, newOrder: q.order }))
        };
        if (reorderInput.orderedQuestionIds.length > 0) {
          await questionService.reorderQuestions(
            template.id,
            reorderInput,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
        }
        // Fetch the template again to ensure order consistency from the backend
        // or trust the local reorder and API success. For robustness, a refetch might be good.
        // For now, we assume the local order + API reorder is sufficient.

        setTimeout(() => {
          setExpandedQuestions((prev) => [...prev, newUiQuestion.id]);
          document
            .getElementById(String(newUiQuestion.id))
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        setSaveStatus('idle');
      } catch (err: any) {
        console.error('Failed to duplicate question:', err);
        setError(err.message || 'Failed to duplicate question.');
        setSaveStatus('error');
      } finally {
        setIsSaving(false);
      }
    },
    [template, appUser, getAccessTokenSilently, getAccessTokenWithPopup]
  );

  const deleteQuestionApi = useCallback(
    async (questionId: number | string) => {
      if (
        !template ||
        !appUser?.id ||
        typeof template.id !== 'number' ||
        typeof questionId !== 'number'
      ) {
        setError('Cannot delete question: Invalid parameters.');
        return;
      }
      setIsSaving(true);
      setSaveStatus('saving');
      setError(null);
      try {
        await questionService.deleteQuestion(
          template.id,
          questionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        let reorderedQuestionsForApi: {
          questionId: number;
          newOrder: number;
        }[] = [];
        setTemplate((prev) => {
          if (!prev) return null;
          const updatedQuestions = prev.questions.filter(
            (q) => q.id !== questionId
          );
          const reorderedLocalQuestions = updatedQuestions.map((q, index) => ({
            ...q,
            order: index
          }));
          reorderedQuestionsForApi = reorderedLocalQuestions
            .filter((q) => typeof q.id === 'number')
            .map((q) => ({ questionId: q.id as number, newOrder: q.order }));

          const newState = { ...prev, questions: reorderedLocalQuestions };
          setOriginalTemplate(JSON.parse(JSON.stringify(newState)));
          return newState;
        });

        if (reorderedQuestionsForApi.length > 0) {
          await questionService.reorderQuestions(
            template.id,
            { orderedQuestionIds: reorderedQuestionsForApi },
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
        }
        setExpandedQuestions((prev) => prev.filter((id) => id !== questionId));
        setSaveStatus('idle');
      } catch (err: any) {
        console.error('Failed to delete question:', err);
        setError(err.message || 'Failed to delete question.');
        setSaveStatus('error');
      } finally {
        setIsSaving(false);
      }
    },
    [template, appUser, getAccessTokenSilently, getAccessTokenWithPopup]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (
        !template ||
        !over ||
        active.id === over.id ||
        !appUser?.id ||
        typeof template.id !== 'number'
      )
        return;

      const oldIndex = template.questions.findIndex((q) => q.id === active.id);
      const newIndex = template.questions.findIndex((q) => q.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reorderedUiQuestions = arrayMove(
        template.questions,
        oldIndex,
        newIndex
      ).map((q, index) => ({ ...q, order: index }));

      setTemplate((prev) => ({ ...prev!, questions: reorderedUiQuestions }));
      setSaveStatus('saving');
      setIsSaving(true);
      setError(null);

      const reorderInput: ReorderQuestionsInput = {
        orderedQuestionIds: reorderedUiQuestions
          .filter((q) => typeof q.id === 'number')
          .map((q) => ({ questionId: q.id as number, newOrder: q.order }))
      };

      if (
        reorderInput.orderedQuestionIds.length === 0 &&
        reorderedUiQuestions.length > 0
      ) {
        // All client-side, just update original
        setOriginalTemplate(
          JSON.parse(
            JSON.stringify({ ...template, questions: reorderedUiQuestions })
          )
        );
        setSaveStatus('idle');
        setIsSaving(false);
        return;
      }
      if (
        reorderInput.orderedQuestionIds.length === 0 &&
        reorderedUiQuestions.length === 0
      ) {
        setIsSaving(false);
        setSaveStatus('idle');
        return;
      }

      try {
        await questionService.reorderQuestions(
          template.id,
          reorderInput,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        // API Success, update originalTemplate to reflect saved state
        setOriginalTemplate(
          JSON.parse(
            JSON.stringify({ ...template, questions: reorderedUiQuestions })
          )
        );
        setSaveStatus('idle');
      } catch (err: any) {
        console.error('Failed to reorder questions:', err);
        setError(err.message || 'Failed to reorder questions.');
        setSaveStatus('error');
        // Revert optimistic update
        setTemplate((prev) => ({
          ...prev!,
          questions: arrayMove(reorderedUiQuestions, newIndex, oldIndex)
        }));
      } finally {
        setIsSaving(false);
      }
    },
    [template, appUser, getAccessTokenSilently, getAccessTokenWithPopup]
  );

  // --- Header Actions ---
  useEffect(() => {
    if (!template) return;
    
    // Show different button based on active tab
    if (activeTab === 'summaries') {
      setMenuActions(
        isMobile ? (
          <IconButton
            onClick={() => summaryListRef.current?.openAddDialog()}
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
              color: 'white',
              borderRadius: '6px',
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
              }
            }}
          >
            <Plus size={18} />
          </IconButton>
        ) : (
          <Button
            variant="contained"
            startIcon={<Plus />}
            onClick={() => summaryListRef.current?.openAddDialog()}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
              }
            }}
          >
            New Summary
          </Button>
        )
      );
    } else if (activeTab === 'questions') {
      setMenuActions(
        isMobile ? (
          <IconButton
            onClick={() => addQuestion(QuestionType.ShortText)}
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
              color: 'white',
              borderRadius: '6px',
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
              }
            }}
          >
            <Plus size={18} />
          </IconButton>
        ) : (
          <Button
            variant="contained"
            startIcon={<Plus />}
            onClick={() => addQuestion(QuestionType.ShortText)} // Default to short text
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
              }
            }}
          >
            New Question
          </Button>
        )
      );
    } else if (activeTab === 'overview') {
      setMenuActions(
        isMobile ? (
          <IconButton
            onClick={() => setIsEditTemplateDialogOpen(true)}
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
              color: 'white',
              borderRadius: '6px',
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
              }
            }}
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setIsEditTemplateDialogOpen(true)}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
              }
            }}
          >
            Edit Template
          </Button>
        )
      );
    } else {
      // No action button for other tabs
      setMenuActions(null);
    }
    
    return () => {
      setMenuActions(null);
    };
  }, [setMenuActions, theme.palette.mode, template, addQuestion, activeTab, isMobile]);

  useEffect(() => {
    setHeaderText(
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          onClick={() => navigate('/organization?tab=templates')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
            transition: 'color 0.15s ease',
            '&:hover': { 
              color: theme.palette.text.primary 
            }
          }}
        >
          <Article size={16} weight="regular" />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'inherit'
            }}
          >
            Templates
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '14px',
            color: theme.palette.text.disabled,
            userSelect: 'none',
            mx: -0.25
          }}
        >
          /
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: theme.palette.text.primary,
            maxWidth: isMobile ? '180px' : 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {template ? template.name : 'Edit Template'}
        </Typography>
      </Box>
    );

    return () => {
      setHeaderText('');
    };
  }, [template?.name, setHeaderText]);

  // --- Render Logic ---
  if (isLoading || (isLoadingCategories && !template)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="calc(100vh - 120px)"
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }} color="text.secondary">
          {isLoading ? 'Loading Template...' : 'Loading Categories...'}
        </Typography>
      </Box>
    );
  }

  if (error && !template) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!template) {
    return (
      <Alert severity="warning" sx={{ m: 3 }}>
        Template data could not be loaded.
      </Alert>
    );
  }

  return (
    <Box sx={{ backgroundColor: theme.palette.background.paper, minHeight: '100vh' }}>
      {error && (saveStatus === 'error' || (!isSaving && !isLoading)) && (
        <Alert
          severity={saveStatus === 'error' ? 'error' : 'warning'}
          sx={{ mb: 2 }}
          onClose={() => {
            setError(null);
            if (saveStatus === 'error') setSaveStatus('idle');
          }}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Box sx={{ mb: 0 }}>
            <UnifiedTabsContainer>
              <UnifiedTabs
                tabs={[
                  { key: 'overview', label: 'Overview' },
                  {
                    key: 'questions',
                    label: 'Questions',
                    count: template?.questions?.length
                  },
                  {
                    key: 'summaries',
                    label: 'Summaries',
                    count: template?.summaries?.length
                  }
                  // Note: Logic tab disabled for now
                ]}
                activeTab={activeTab}
                onTabChange={(key) => setActiveTab(key as string)}
              />
            </UnifiedTabsContainer>
          </Box>

          {activeTab === 'questions' && (
            <QuestionList
              template={template}
              filteredQuestions={filteredQuestions}
              filteredQuestionIds={filteredQuestionIds}
              expandedQuestions={expandedQuestions}
              questionSearchTerm={questionSearchTerm}
              onDragEnd={handleDragEnd}
              onLocalQuestionChange={handleLocalQuestionChange}
              onPersistQuestion={handlePersistQuestion}
              onDuplicateQuestion={duplicateQuestion}
              onDeleteQuestion={deleteQuestionApi}
              onToggleExpandQuestion={toggleQuestionExpand}
              onAddQuestion={addQuestion}
              onSearchChange={setQuestionSearchTerm}
              onExpandAllQuestions={expandAllQuestions}
              onCollapseAllQuestions={collapseAllQuestions}
            />
          )}

          {activeTab === 'summaries' && (
            <SummaryList
              ref={summaryListRef}
              templateId={template.id}
              summaries={template.summaries || []}
              companyId={template.companyId}
              userId={appUser?.id}
              onAddSummary={handleAddSummary}
              onUpdateSummary={handleUpdateSummary}
              onDeleteSummary={handleDeleteSummary}
              onSummaryGroupsUpdated={handleSummaryGroupsUpdated}
              isLoading={saveStatus === 'saving'}
              saveStatus={saveStatus}
            />
          )}

          {activeTab === 'settings' && (
            <TemplateSettingsForm
              template={template}
              templateCategories={templateCategories}
              isLoadingCategories={isLoadingCategories}
              isSaving={isSaving} // Pass general isSaving flag
              onUpdateDetailsLocal={updateTemplateDetailsLocal}
              onSaveSettings={handleSaveTemplateSettings}
            />
          )}

          {activeTab === 'overview' && (
            <Box sx={{ px: 3, pt: 3 }}>
              <TemplateSummaryCard
                template={template}
                templateCategories={templateCategories}
                saveStatus={saveStatus}
                hasChanges={hasChanges}
                onSave={handleSaveTemplateSettings}
              />
            </Box>
          )}

          {activeTab === 'logic' && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Conditional Logic
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="body2" color="text.secondary" paragraph>
                  Conditional logic allows you to show or hide questions based
                  on answers to previous questions. (Feature coming soon)
                </Typography>
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body1" gutterBottom>
                    Logic configuration not yet available
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    sx={{ mt: 2 }}
                    disabled
                  >
                    Add Logic Rule
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Edit Template Dialog */}
      <Dialog
        open={isEditTemplateDialogOpen}
        onClose={() => setIsEditTemplateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Template Details</DialogTitle>
        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          <TemplateSettingsForm
            template={template}
            templateCategories={templateCategories}
            isLoadingCategories={isLoadingCategories}
            isSaving={isSaving}
            onUpdateDetailsLocal={updateTemplateDetailsLocal}
            onSaveSettings={handleSaveTemplateSettings}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button 
            onClick={() => setIsEditTemplateDialogOpen(false)}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 500,
              px: 2.5,
              py: 0.75,
              color: theme.palette.text.secondary
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              await handleSaveTemplateSettings();
              setIsEditTemplateDialogOpen(false);
            }}
            disabled={!hasChanges || isSaving}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 0.75,
              backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
              },
              '&:disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateEditPage;
